import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Camera, Upload, Download, X, Car, Fuel, Gauge, 
  CheckCircle, Send, FileText, Pen, Clock, User, Building2, AlertTriangle,
  CreditCard, IdCard, Check, ExternalLink, DollarSign, Droplets, Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import SignatureCanvas from './SignatureCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Damage types with colors
const DAMAGE_TYPES = [
  { id: 'scratch', label: 'Scratch', labelFr: 'Rayure', color: '#FF6B6B' },
  { id: 'dent', label: 'Dent', labelFr: 'Bosse', color: '#FFA500' },
  { id: 'chip', label: 'Chip/Stone', labelFr: 'Éclat', color: '#FFD93D' },
  { id: 'crack', label: 'Crack', labelFr: 'Fissure', color: '#C70039' },
  { id: 'other', label: 'Other', labelFr: 'Autre', color: '#9B59B6' },
];

const FUEL_LEVELS = ['empty', '1/4', '1/2', '3/4', 'full'];

// Interactive Vehicle Diagram
const VehicleDiagram = ({ damageMarks = [], onAddMark, onRemoveMark, readOnly = false }) => {
  const svgRef = useRef(null);
  const [selectedType, setSelectedType] = useState('scratch');
  const [hoveredMark, setHoveredMark] = useState(null);

  const handleSvgClick = (e) => {
    if (readOnly) return;
    
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    onAddMark({
      x: Math.round(x),
      y: Math.round(y),
      type: selectedType,
      description: ''
    });
  };

  return (
    <div className="space-y-3">
      {!readOnly && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-gray-500 text-sm">Add damage:</span>
          {DAMAGE_TYPES.map(type => (
            <button
              key={type.id}
              type="button"
              onClick={() => setSelectedType(type.id)}
              className={`px-3 py-1 rounded-sm text-xs font-medium transition-all ${
                selectedType === type.id 
                  ? 'ring-2 ring-primary ring-offset-2' 
                  : 'opacity-70 hover:opacity-100'
              }`}
              style={{ backgroundColor: type.color, color: '#fff' }}
            >
              {type.label}
            </button>
          ))}
        </div>
      )}
      
      <div 
        className="relative bg-gray-100 rounded-lg border-2 border-gray-300 cursor-crosshair overflow-hidden"
        style={{ paddingBottom: '75%' }}
      >
        <svg 
          ref={svgRef}
          viewBox="0 0 100 75"
          className="absolute inset-0 w-full h-full"
          onClick={handleSvgClick}
        >
          {/* Background */}
          <rect x="0" y="0" width="100" height="75" fill="#F3F4F6"/>
          
          {/* Main car body - top view */}
          <g>
            {/* Car body shadow */}
            <rect x="17" y="9" width="66" height="58" rx="8" fill="#D1D5DB"/>
            
            {/* Main body */}
            <rect x="15" y="7" width="70" height="61" rx="10" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="1"/>
            
            {/* Hood (front) */}
            <rect x="22" y="10" width="56" height="10" rx="4" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
            
            {/* Front bumper area */}
            <rect x="25" y="7" width="50" height="4" rx="2" fill="#9CA3AF"/>
            
            {/* Trunk (rear) */}
            <rect x="22" y="55" width="56" height="10" rx="4" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
            
            {/* Rear bumper area */}
            <rect x="25" y="64" width="50" height="4" rx="2" fill="#9CA3AF"/>
            
            {/* Front windshield */}
            <rect x="26" y="18" width="48" height="8" rx="3" fill="#87CEEB" stroke="#60A5FA" strokeWidth="0.8"/>
            <line x1="50" y1="18" x2="50" y2="26" stroke="#60A5FA" strokeWidth="0.3" strokeDasharray="1,1"/>
            
            {/* Roof / cabin area */}
            <rect x="23" y="26" width="54" height="22" rx="2" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
            
            {/* Rear windshield */}
            <rect x="26" y="47" width="48" height="8" rx="3" fill="#87CEEB" stroke="#60A5FA" strokeWidth="0.8"/>
            <line x1="50" y1="47" x2="50" y2="55" stroke="#60A5FA" strokeWidth="0.3" strokeDasharray="1,1"/>
            
            {/* Left side windows */}
            <rect x="16" y="29" width="4" height="16" rx="1.5" fill="#87CEEB" stroke="#60A5FA" strokeWidth="0.5"/>
            
            {/* Right side windows */}
            <rect x="80" y="29" width="4" height="16" rx="1.5" fill="#87CEEB" stroke="#60A5FA" strokeWidth="0.5"/>
            
            {/* Front headlights */}
            <ellipse cx="28" cy="9" rx="4" ry="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="0.5"/>
            <ellipse cx="72" cy="9" rx="4" ry="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="0.5"/>
            
            {/* Rear lights */}
            <ellipse cx="28" cy="66" rx="4" ry="2" fill="#FCA5A5" stroke="#EF4444" strokeWidth="0.5"/>
            <ellipse cx="72" cy="66" rx="4" ry="2" fill="#FCA5A5" stroke="#EF4444" strokeWidth="0.5"/>
            
            {/* Front left wheel */}
            <rect x="8" y="14" width="10" height="14" rx="3" fill="#1F2937" stroke="#111827" strokeWidth="0.8"/>
            <rect x="10" y="17" width="6" height="8" rx="1" fill="#374151"/>
            
            {/* Front right wheel */}
            <rect x="82" y="14" width="10" height="14" rx="3" fill="#1F2937" stroke="#111827" strokeWidth="0.8"/>
            <rect x="84" y="17" width="6" height="8" rx="1" fill="#374151"/>
            
            {/* Rear left wheel */}
            <rect x="8" y="47" width="10" height="14" rx="3" fill="#1F2937" stroke="#111827" strokeWidth="0.8"/>
            <rect x="10" y="50" width="6" height="8" rx="1" fill="#374151"/>
            
            {/* Rear right wheel */}
            <rect x="82" y="47" width="10" height="14" rx="3" fill="#1F2937" stroke="#111827" strokeWidth="0.8"/>
            <rect x="84" y="50" width="6" height="8" rx="1" fill="#374151"/>
            
            {/* Left side mirror */}
            <ellipse cx="13" cy="22" rx="3" ry="1.5" fill="#6B7280" stroke="#4B5563" strokeWidth="0.3"/>
            
            {/* Right side mirror */}
            <ellipse cx="87" cy="22" rx="3" ry="1.5" fill="#6B7280" stroke="#4B5563" strokeWidth="0.3"/>
            
            {/* Center line on hood */}
            <line x1="50" y1="10" x2="50" y2="18" stroke="#9CA3AF" strokeWidth="0.3" strokeDasharray="2,1"/>
            
            {/* Door handles */}
            <rect x="17" y="33" width="3" height="1" rx="0.5" fill="#6B7280"/>
            <rect x="17" y="40" width="3" height="1" rx="0.5" fill="#6B7280"/>
            <rect x="80" y="33" width="3" height="1" rx="0.5" fill="#6B7280"/>
            <rect x="80" y="40" width="3" height="1" rx="0.5" fill="#6B7280"/>
          </g>
          
          {/* Labels */}
          <text x="50" y="4" textAnchor="middle" fill="#374151" fontSize="4" fontWeight="bold">AVANT / FRONT</text>
          <text x="50" y="73" textAnchor="middle" fill="#374151" fontSize="4" fontWeight="bold">ARRIÈRE / REAR</text>
          <text x="4" y="38" textAnchor="middle" fill="#374151" fontSize="3" fontWeight="bold">G</text>
          <text x="96" y="38" textAnchor="middle" fill="#374151" fontSize="3" fontWeight="bold">D</text>
          
          {/* Damage marks */}
          {damageMarks.map((mark, index) => {
            const damageType = DAMAGE_TYPES.find(t => t.id === mark.type) || DAMAGE_TYPES[4];
            return (
              <g 
                key={index}
                onMouseEnter={() => setHoveredMark(index)}
                onMouseLeave={() => setHoveredMark(null)}
                style={{ cursor: readOnly ? 'default' : 'pointer' }}
                onClick={(e) => {
                  if (!readOnly) {
                    e.stopPropagation();
                    onRemoveMark(index);
                  }
                }}
              >
                {/* Shadow for better visibility */}
                <circle 
                  cx={mark.x + 0.3} 
                  cy={mark.y + 0.3} 
                  r="3.5"
                  fill="rgba(0,0,0,0.3)"
                />
                <circle 
                  cx={mark.x} 
                  cy={mark.y} 
                  r="3.5"
                  fill={damageType.color}
                  stroke="#fff"
                  strokeWidth="0.8"
                />
                <text
                  x={mark.x}
                  y={mark.y + 1.2}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="4"
                  fontWeight="bold"
                >
                  {index + 1}
                </text>
                {hoveredMark === index && !readOnly && (
                  <text
                    x={mark.x}
                    y={mark.y - 5}
                    textAnchor="middle"
                    fill="#EF4444"
                    fontSize="3"
                    fontWeight="bold"
                  >
                    ✕ Click to remove
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      
      {!readOnly && (
        <p className="text-gray-400 text-xs">
          Click on the diagram to add a damage mark. Click a mark to remove it.
        </p>
      )}
    </div>
  );
};

// Fuel Gauge Component
const FuelGauge = ({ value, onChange, readOnly = false }) => {
  return (
    <div className="space-y-2">
      <Label className="text-gray-500">Fuel Level</Label>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-8 bg-neutral-800 rounded-sm overflow-hidden border border-gray-200 relative">
          {[0.25, 0.5, 0.75].map(pos => (
            <div 
              key={pos} 
              className="absolute top-0 bottom-0 w-px bg-white/20"
              style={{ left: `${pos * 100}%` }}
            />
          ))}
          <div 
            className="h-full bg-primary transition-all"
            style={{ 
              width: `${FUEL_LEVELS.indexOf(value) / (FUEL_LEVELS.length - 1) * 100}%` 
            }}
          />
          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs text-gray-500">
            <span>E</span>
            <span>F</span>
          </div>
        </div>
        {!readOnly ? (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="input-default w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {FUEL_LEVELS.map(level => (
                <SelectItem key={level} value={level} className="text-[#181B1F]">
                  {level === 'empty' ? 'Empty' : level === 'full' ? 'Full' : level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-[#181B1F] w-16 text-center">
            {value === 'empty' ? 'Empty' : value === 'full' ? 'Full' : value}
          </span>
        )}
      </div>
    </div>
  );
};

// Photo Upload Component
const PhotoUpload = ({ photos = [], onUpload, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    for (const file of files) {
      await onUpload(file);
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-gray-500">Condition Photos</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn-outline"
        >
          <Camera className="w-4 h-4 mr-2" />
          {uploading ? 'Uploading...' : 'Add Photo'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
      
      {photos.length === 0 ? (
        <div className="bg-neutral-800 border border-dashed border-gray-300 rounded-sm p-6 text-center">
          <Camera className="w-8 h-8 text-white/20 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No photos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative aspect-square bg-neutral-800 rounded-sm overflow-hidden group">
              <img 
                src={`${API_URL}${photo}`}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-[#181B1F]" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Interactive Report Generator
const InteractiveReportGenerator = ({ 
  booking, 
  reportType = 'delivery', // 'delivery' or 'return'
  onClose, 
  onComplete 
}) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1: Condition, 2: Review & Sign
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [condition, setCondition] = useState({
    odometer: '',
    fuel_level: 'full',
    damage_marks: [],
    notes: ''
  });
  const [photos, setPhotos] = useState([]);
  const [adminSignature, setAdminSignature] = useState(null);
  const [customerSignature, setCustomerSignature] = useState(null);
  
  // Return options
  const [returnWithoutCustomer, setReturnWithoutCustomer] = useState(false);
  const [returnAcknowledgment, setReturnAcknowledgment] = useState(false);
  const [canSkipCustomerSignature, setCanSkipCustomerSignature] = useState(false);
  
  // Document verification (for delivery report)
  const [idVerified, setIdVerified] = useState(false);
  const [licenseVerified, setLicenseVerified] = useState(false);
  
  // Extra km calculation (for return)
  const [extraKmData, setExtraKmData] = useState(null);
  
  // Additional charges (for return report - no automatic payment, just documentation)
  const [additionalCharges, setAdditionalCharges] = useState({
    // Damage handling: 'none', 'amount', 'bodyshop_quote', 'franchise'
    damageOption: 'none',
    damageAmount: '',
    // Fuel difference
    fuelDifference: false,
    fuelLiters: '',
    fuelPricePerLiter: 1.99,
    // Cleanliness
    vehicleClean: true,
    cleaningFee: 100
  });
  
  // Pickup condition for comparison
  const [pickupCondition, setPickupCondition] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchExistingCondition();
  }, [booking.id, reportType]);

  const fetchExistingCondition = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${booking.id}/condition-photos`,
        { headers }
      );
      
      const conditionKey = reportType === 'delivery' ? 'pickup_condition' : 'return_condition';
      const photosKey = reportType === 'delivery' ? 'pickup_photos' : 'return_photos';
      
      if (response.data[conditionKey]) {
        setCondition({
          odometer: response.data[conditionKey].odometer || '',
          fuel_level: response.data[conditionKey].fuel_level || 'full',
          damage_marks: response.data[conditionKey].damage_marks || [],
          notes: response.data[conditionKey].notes || ''
        });
      }
      
      // For return report, pre-fill with pickup condition damage marks if no return marks exist
      if (reportType === 'return' && !response.data.return_condition?.damage_marks?.length) {
        if (response.data.pickup_condition?.damage_marks?.length) {
          setCondition(prev => ({
            ...prev,
            damage_marks: [...response.data.pickup_condition.damage_marks]
          }));
        }
      }
      
      // Store pickup condition for comparison (return report)
      if (reportType === 'return' && response.data.pickup_condition) {
        setPickupCondition(response.data.pickup_condition);
      }
      
      // Check if return without customer is allowed
      if (reportType === 'return') {
        setCanSkipCustomerSignature(booking.return_without_customer || false);
      }
      
      // Load existing document verification status (for delivery report)
      if (reportType === 'delivery') {
        setIdVerified(booking.id_verified || false);
        setLicenseVerified(booking.license_verified || false);
      }
      
      setPhotos(response.data[photosKey] || []);
    } catch (error) {
      console.error('Error fetching condition:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate extra km when return odometer is set
  const calculateExtraKm = async () => {
    if (reportType !== 'return' || !condition.odometer) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/${booking.id}/calculate-extra-km`,
        {},
        { headers }
      );
      setExtraKmData(response.data);
    } catch (error) {
      console.error('Error calculating extra km:', error);
    }
  };
  
  useEffect(() => {
    if (reportType === 'return' && condition.odometer && step === 2) {
      calculateExtraKm();
    }
  }, [step, condition.odometer]);

  const handlePhotoUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', reportType === 'delivery' ? 'pickup' : 'return');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/bookings/${booking.id}/condition-photos`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setPhotos(prev => [...prev, response.data.url]);
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const addDamageMark = (mark) => {
    setCondition(prev => ({
      ...prev,
      damage_marks: [...prev.damage_marks, mark]
    }));
  };

  const removeDamageMark = (index) => {
    setCondition(prev => ({
      ...prev,
      damage_marks: prev.damage_marks.filter((_, i) => i !== index)
    }));
  };

  const updateDamageDescription = (index, description) => {
    setCondition(prev => ({
      ...prev,
      damage_marks: prev.damage_marks.map((mark, i) => 
        i === index ? { ...mark, description } : mark
      )
    }));
  };

  const handleNextStep = async () => {
    // Save condition before moving to signatures
    try {
      const conditionType = reportType === 'delivery' ? 'pickup' : 'return';
      await axios.put(
        `${API_URL}/api/admin/bookings/${booking.id}/condition/${conditionType}`,
        {
          odometer: condition.odometer ? parseInt(condition.odometer) : null,
          fuel_level: condition.fuel_level,
          damage_marks: condition.damage_marks,
          notes: condition.notes
        },
        { headers }
      );
      
      // Save document verification status for delivery report
      if (reportType === 'delivery') {
        await axios.post(
          `${API_URL}/api/admin/bookings/${booking.id}/verify-documents`,
          null,
          { 
            headers, 
            params: { 
              id_verified: idVerified, 
              license_verified: licenseVerified 
            } 
          }
        );
      }
      
      setStep(2);
    } catch (error) {
      console.error('Error saving condition:', error);
      toast.error('Failed to save condition');
    }
  };

  const handleSubmit = async () => {
    // For delivery report: customer signature required unless acknowledging return without customer
    // For return report: customer signature not required if return_without_customer was set
    const needsCustomerSignature = reportType === 'delivery' 
      ? !returnAcknowledgment 
      : !canSkipCustomerSignature;
    
    if (!adminSignature) {
      toast.error('Admin signature is required');
      return;
    }
    
    if (needsCustomerSignature && !customerSignature) {
      toast.error('Customer signature is required');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare additional charges data for return report
      const additionalChargesData = reportType === 'return' ? {
        extra_km: extraKmData?.extra_km || 0,
        extra_km_charge: extraKmData?.extra_charge || 0,
        damage_option: additionalCharges.damageOption,
        damage_amount: additionalCharges.damageOption === 'amount' ? parseFloat(additionalCharges.damageAmount || 0) : 0,
        damage_franchise: additionalCharges.damageOption === 'franchise' ? (booking.vehicle_franchise || 1500) : 0,
        fuel_difference: additionalCharges.fuelDifference,
        fuel_liters: additionalCharges.fuelDifference ? parseFloat(additionalCharges.fuelLiters || 0) : 0,
        fuel_charge: additionalCharges.fuelDifference ? parseFloat(additionalCharges.fuelLiters || 0) * 1.99 : 0,
        vehicle_clean: additionalCharges.vehicleClean,
        cleaning_fee: !additionalCharges.vehicleClean ? 100 : 0,
        total_additional_charges: (
          (extraKmData?.extra_charge || 0) +
          (additionalCharges.damageOption === 'amount' ? parseFloat(additionalCharges.damageAmount || 0) : 0) +
          (additionalCharges.damageOption === 'franchise' ? (booking.vehicle_franchise || 1500) : 0) +
          (additionalCharges.fuelDifference ? parseFloat(additionalCharges.fuelLiters || 0) * 1.99 : 0) +
          (!additionalCharges.vehicleClean ? 100 : 0)
        )
      } : null;

      // Submit the signed report
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/${booking.id}/sign-report`,
        {
          report_type: reportType,
          admin_signature: adminSignature,
          customer_signature: customerSignature,
          condition: {
            odometer: condition.odometer ? parseInt(condition.odometer) : null,
            fuel_level: condition.fuel_level,
            damage_marks: condition.damage_marks,
            notes: condition.notes
          },
          send_email: true,
          return_without_customer: reportType === 'delivery' ? returnWithoutCustomer : false,
          return_acknowledgment: returnAcknowledgment,
          additional_charges: additionalChargesData
        },
        { headers }
      );

      toast.success(
        reportType === 'delivery' 
          ? 'Delivery report signed and sent to customer!' 
          : 'Return report signed and sent to customer!'
      );

      // Note: Status is already updated by the sign-report endpoint
      // No need to make additional status update calls

      if (onComplete) onComplete();
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const title = reportType === 'delivery' 
    ? 'Delivery Report / PV de Livraison' 
    : 'Return Report / PV de Restitution';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div>
          <h2 className="text-xl text-[#181B1F] font-serif">{title}</h2>
          <p className="text-gray-500 text-sm">{booking.vehicle_name} • {booking.customer_info?.full_name}</p>
        </div>
        <Badge className={step === 1 ? 'bg-primary/20 text-primary' : 'bg-green-500/20 text-green-400'}>
          Step {step} of 2
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-2">
        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-100'}`}>
            <Car className="w-4 h-4" />
          </div>
          <span className="text-sm">Condition</span>
        </div>
        <div className={`flex-1 h-px ${step >= 2 ? 'bg-primary' : 'bg-gray-100'}`} />
        <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-100'}`}>
            <Pen className="w-4 h-4" />
          </div>
          <span className="text-sm">Sign</span>
        </div>
      </div>

      {/* Step 1: Condition Recording */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Diagram */}
            <div className="space-y-4">
              <h3 className="text-[#181B1F] font-medium flex items-center gap-2">
                <Car className="w-4 h-4 text-primary" />
                Vehicle Condition Diagram
              </h3>
              <VehicleDiagram
                damageMarks={condition.damage_marks}
                onAddMark={addDamageMark}
                onRemoveMark={removeDamageMark}
              />
              
              {/* Damage List */}
              {condition.damage_marks.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-500 text-sm">Damage Details</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {condition.damage_marks.map((mark, index) => {
                      const damageType = DAMAGE_TYPES.find(t => t.id === mark.type);
                      return (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-sm">
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center text-[#181B1F] text-xs font-bold shrink-0"
                            style={{ backgroundColor: damageType?.color }}
                          >
                            {index + 1}
                          </div>
                          <Input
                            value={mark.description}
                            onChange={(e) => updateDamageDescription(index, e.target.value)}
                            className="input-default flex-1 h-8 text-sm"
                            placeholder={`Describe ${damageType?.label || 'damage'}...`}
                          />
                          <button
                            type="button"
                            onClick={() => removeDamageMark(index)}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Readings & Photos */}
            <div className="space-y-4">
              <h3 className="text-[#181B1F] font-medium flex items-center gap-2">
                <Gauge className="w-4 h-4 text-primary" />
                Readings & Photos
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Odometer (km)</Label>
                  <Input
                    type="number"
                    value={condition.odometer}
                    onChange={(e) => setCondition(prev => ({ ...prev, odometer: e.target.value }))}
                    className="input-default"
                    placeholder="Current reading"
                    data-testid="odometer-input"
                  />
                </div>
                
                <FuelGauge
                  value={condition.fuel_level}
                  onChange={(v) => setCondition(prev => ({ ...prev, fuel_level: v }))}
                />
              </div>

              <PhotoUpload
                photos={photos}
                onUpload={handlePhotoUpload}
                onRemove={handleRemovePhoto}
              />

              <div className="space-y-2">
                <Label className="text-gray-500">Notes / Remarks</Label>
                <Textarea
                  value={condition.notes}
                  onChange={(e) => setCondition(prev => ({ ...prev, notes: e.target.value }))}
                  className="input-default"
                  placeholder="Additional notes about vehicle condition..."
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
              
              {/* Document Verification (only for delivery report) */}
              {reportType === 'delivery' && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4 space-y-4">
                  <div className="flex items-start gap-2">
                    <IdCard className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="text-blue-400 font-medium">Document Verification</h4>
                      <p className="text-blue-400/70 text-sm">Verify customer documents with originals</p>
                    </div>
                  </div>
                  
                  {/* Customer Documents Display */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* ID Document */}
                    <div className="bg-gray-50 rounded-sm p-3 space-y-2">
                      <Label className="text-gray-500 text-sm flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        ID Document
                      </Label>
                      {booking.customer_info?.id_document_url ? (
                        <div className="space-y-2">
                          <a 
                            href={`${API_URL}${booking.customer_info.id_document_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm"
                            data-testid="view-id-document"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View uploaded document
                          </a>
                          <img 
                            src={`${API_URL}${booking.customer_info.id_document_url}`}
                            alt="ID Document"
                            className="w-full h-24 object-cover rounded-sm cursor-pointer hover:opacity-80"
                            onClick={() => window.open(`${API_URL}${booking.customer_info.id_document_url}`, '_blank')}
                          />
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm">No document uploaded</p>
                      )}
                      <div className="flex items-start gap-3 pt-2 border-t border-gray-200">
                        <Checkbox 
                          id="id-verified"
                          checked={idVerified}
                          onCheckedChange={setIdVerified}
                          className="mt-0.5"
                          data-testid="checkbox-id-verified"
                        />
                        <label htmlFor="id-verified" className="text-gray-700 text-sm">
                          <span className={idVerified ? 'text-green-400 font-medium' : ''}>
                            {idVerified ? '✓ ' : ''}Verified with original ID
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Driver's License */}
                    <div className="bg-gray-50 rounded-sm p-3 space-y-2">
                      <Label className="text-gray-500 text-sm flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        Driver's License
                      </Label>
                      {booking.customer_info?.license_document_url ? (
                        <div className="space-y-2">
                          <a 
                            href={`${API_URL}${booking.customer_info.license_document_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm"
                            data-testid="view-license-document"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View uploaded document
                          </a>
                          <img 
                            src={`${API_URL}${booking.customer_info.license_document_url}`}
                            alt="Driver's License"
                            className="w-full h-24 object-cover rounded-sm cursor-pointer hover:opacity-80"
                            onClick={() => window.open(`${API_URL}${booking.customer_info.license_document_url}`, '_blank')}
                          />
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm">No document uploaded</p>
                      )}
                      <div className="flex items-start gap-3 pt-2 border-t border-gray-200">
                        <Checkbox 
                          id="license-verified"
                          checked={licenseVerified}
                          onCheckedChange={setLicenseVerified}
                          className="mt-0.5"
                          data-testid="checkbox-license-verified"
                        />
                        <label htmlFor="license-verified" className="text-gray-700 text-sm">
                          <span className={licenseVerified ? 'text-green-400 font-medium' : ''}>
                            {licenseVerified ? '✓ ' : ''}Verified with original driver's license
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Verification Warning */}
                  {(!idVerified || !licenseVerified) && (
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Please verify both documents with the original before delivery</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Return Options (only for delivery report) */}
              {reportType === 'delivery' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="text-amber-400 font-medium">Late/Overnight Return Options</h4>
                      <p className="text-amber-400/70 text-sm">For returns outside agency hours (e.g., 2:00 AM)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="return-without-customer"
                      checked={returnWithoutCustomer}
                      onCheckedChange={setReturnWithoutCustomer}
                      className="mt-1"
                    />
                    <label htmlFor="return-without-customer" className="text-gray-700 text-sm">
                      Vehicle will be returned outside agency hours. The return inspection (PV de restitution) 
                      will be done by the agency without the customer present.
                    </label>
                  </div>
                  
                  {returnWithoutCustomer && (
                    <div className="flex items-start gap-3 ml-6">
                      <Checkbox 
                        id="return-acknowledgment"
                        checked={returnAcknowledgment}
                        onCheckedChange={setReturnAcknowledgment}
                        className="mt-1"
                      />
                      <label htmlFor="return-acknowledgment" className="text-gray-700 text-sm">
                        <strong>Customer acknowledges</strong> that the return report will be completed without 
                        their signature and accepts the findings of the agency inspection.
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Additional Charges Section (only for return report) */}
              {reportType === 'return' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 space-y-4">
                  <div className="flex items-start gap-2">
                    <DollarSign className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="text-red-400 font-medium">Frais Supplémentaires / Additional Charges</h4>
                      <p className="text-red-400/70 text-sm">Documentation uniquement - pas de paiement automatique</p>
                    </div>
                  </div>
                  
                  {/* Damage Charges */}
                  {condition.damage_marks.length > (pickupCondition?.damage_marks?.length || 0) && (
                    <div className="space-y-3 border-t border-red-500/20 pt-3">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-red-400" />
                        <Label className="text-red-400 font-medium">Dommages / Damages</Label>
                        <Badge className="bg-red-500/20 text-red-400 text-xs">
                          {condition.damage_marks.length - (pickupCondition?.damage_marks?.length || 0)} nouveau(x)
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2">
                        <label className="flex items-center gap-3 p-2 bg-white rounded-sm border cursor-pointer hover:border-red-300">
                          <input
                            type="radio"
                            name="damageOption"
                            value="amount"
                            checked={additionalCharges.damageOption === 'amount'}
                            onChange={(e) => setAdditionalCharges(prev => ({ ...prev, damageOption: e.target.value }))}
                            className="text-red-500"
                          />
                          <span className="text-sm text-gray-700">Montant chiffré:</span>
                          <Input
                            type="number"
                            value={additionalCharges.damageAmount}
                            onChange={(e) => setAdditionalCharges(prev => ({ ...prev, damageAmount: e.target.value, damageOption: 'amount' }))}
                            className="input-default w-32 h-8"
                            placeholder="CHF"
                          />
                        </label>
                        
                        <label className="flex items-center gap-3 p-2 bg-white rounded-sm border cursor-pointer hover:border-red-300">
                          <input
                            type="radio"
                            name="damageOption"
                            value="bodyshop_quote"
                            checked={additionalCharges.damageOption === 'bodyshop_quote'}
                            onChange={(e) => setAdditionalCharges(prev => ({ ...prev, damageOption: e.target.value }))}
                            className="text-red-500"
                          />
                          <span className="text-sm text-gray-700">À chiffrer par devis carrosserie</span>
                        </label>
                        
                        <label className="flex items-center gap-3 p-2 bg-white rounded-sm border cursor-pointer hover:border-red-300">
                          <input
                            type="radio"
                            name="damageOption"
                            value="franchise"
                            checked={additionalCharges.damageOption === 'franchise'}
                            onChange={(e) => setAdditionalCharges(prev => ({ ...prev, damageOption: e.target.value }))}
                            className="text-red-500"
                          />
                          <span className="text-sm text-gray-700">Franchise véhicule (coût supérieur): CHF {booking.vehicle_franchise || 1500}</span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {/* Fuel Difference */}
                  <div className="space-y-3 border-t border-red-500/20 pt-3">
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-amber-500" />
                      <Label className="text-gray-700 font-medium">Carburant / Fuel</Label>
                      {pickupCondition && (
                        <span className="text-xs text-gray-500">
                          (Départ: {pickupCondition.fuel_level} → Retour: {condition.fuel_level})
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="fuel-difference"
                        checked={additionalCharges.fuelDifference}
                        onCheckedChange={(v) => setAdditionalCharges(prev => ({ ...prev, fuelDifference: v }))}
                        className="mt-1"
                      />
                      <div className="flex-1 space-y-2">
                        <label htmlFor="fuel-difference" className="text-sm text-gray-700">
                          Différence de carburant à facturer
                        </label>
                        
                        {additionalCharges.fuelDifference && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={additionalCharges.fuelLiters}
                              onChange={(e) => setAdditionalCharges(prev => ({ ...prev, fuelLiters: e.target.value }))}
                              className="input-default w-24 h-8"
                              placeholder="Litres"
                            />
                            <span className="text-sm text-gray-500">litres × CHF 1.99 =</span>
                            <span className="text-sm font-bold text-red-500">
                              CHF {(parseFloat(additionalCharges.fuelLiters || 0) * 1.99).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Cleanliness */}
                  <div className="space-y-3 border-t border-red-500/20 pt-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-blue-500" />
                      <Label className="text-gray-700 font-medium">Propreté / Cleanliness</Label>
                    </div>
                    
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 p-2 bg-white rounded-sm border cursor-pointer hover:border-green-300">
                        <input
                          type="radio"
                          name="vehicleClean"
                          checked={additionalCharges.vehicleClean}
                          onChange={() => setAdditionalCharges(prev => ({ ...prev, vehicleClean: true }))}
                          className="text-green-500"
                        />
                        <span className="text-sm text-gray-700">✓ Propre</span>
                      </label>
                      
                      <label className="flex items-center gap-2 p-2 bg-white rounded-sm border cursor-pointer hover:border-red-300">
                        <input
                          type="radio"
                          name="vehicleClean"
                          checked={!additionalCharges.vehicleClean}
                          onChange={() => setAdditionalCharges(prev => ({ ...prev, vehicleClean: false }))}
                          className="text-red-500"
                        />
                        <span className="text-sm text-gray-700">✗ Sale (CHF 100)</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Total Additional Charges Summary */}
                  {(extraKmData?.extra_charge > 0 || 
                    additionalCharges.damageOption !== 'none' || 
                    additionalCharges.fuelDifference || 
                    !additionalCharges.vehicleClean) && (
                    <div className="bg-red-500/20 rounded-sm p-3 mt-4">
                      <h5 className="text-red-400 font-bold mb-2">Récapitulatif des Frais</h5>
                      <div className="space-y-1 text-sm">
                        {extraKmData?.extra_charge > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Km excédentaires ({extraKmData.extra_km} km)</span>
                            <span className="text-red-500 font-medium">CHF {extraKmData.extra_charge.toFixed(2)}</span>
                          </div>
                        )}
                        {additionalCharges.damageOption === 'amount' && additionalCharges.damageAmount && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Dommages (montant chiffré)</span>
                            <span className="text-red-500 font-medium">CHF {parseFloat(additionalCharges.damageAmount).toFixed(2)}</span>
                          </div>
                        )}
                        {additionalCharges.damageOption === 'bodyshop_quote' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Dommages</span>
                            <span className="text-amber-500 font-medium">Devis carrosserie à établir</span>
                          </div>
                        )}
                        {additionalCharges.damageOption === 'franchise' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Dommages (franchise)</span>
                            <span className="text-red-500 font-medium">CHF {booking.vehicle_franchise || 1500}</span>
                          </div>
                        )}
                        {additionalCharges.fuelDifference && additionalCharges.fuelLiters > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Carburant ({additionalCharges.fuelLiters} L)</span>
                            <span className="text-red-500 font-medium">CHF {(parseFloat(additionalCharges.fuelLiters) * 1.99).toFixed(2)}</span>
                          </div>
                        )}
                        {!additionalCharges.vehicleClean && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Nettoyage (véhicule sale)</span>
                            <span className="text-red-500 font-medium">CHF 100.00</span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-red-500/30 font-bold">
                          <span className="text-gray-700">TOTAL (hors devis)</span>
                          <span className="text-red-600">
                            CHF {(
                              (extraKmData?.extra_charge || 0) +
                              (additionalCharges.damageOption === 'amount' ? parseFloat(additionalCharges.damageAmount || 0) : 0) +
                              (additionalCharges.damageOption === 'franchise' ? (booking.vehicle_franchise || 1500) : 0) +
                              (additionalCharges.fuelDifference ? parseFloat(additionalCharges.fuelLiters || 0) * 1.99 : 0) +
                              (!additionalCharges.vehicleClean ? 100 : 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="btn-outline">
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleNextStep} 
              className="btn-primary"
              data-testid="continue-to-signatures"
            >
              Continue to Signatures
              <Pen className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Review & Sign */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Vehicle Diagram Summary */}
          <div className="bg-gray-50 rounded-sm p-4 space-y-3">
            <h3 className="text-[#181B1F] font-medium flex items-center gap-2">
              <Car className="w-4 h-4 text-primary" />
              Vehicle Condition Overview
            </h3>
            <div className="flex justify-center">
              <div className="w-64">
                <VehicleDiagram
                  damageMarks={condition.damage_marks}
                  readOnly={true}
                />
              </div>
            </div>
            {condition.damage_marks.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center">
                {condition.damage_marks.map((mark, index) => {
                  const damageType = DAMAGE_TYPES.find(t => t.id === mark.type);
                  return (
                    <div key={index} className="flex items-center gap-1 text-xs bg-white px-2 py-1 rounded-full border">
                      <div 
                        className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ backgroundColor: damageType?.color }}
                      >
                        {index + 1}
                      </div>
                      <span className="text-gray-600">{damageType?.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-sm p-4 space-y-3">
            <h3 className="text-[#181B1F] font-medium">Report Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Vehicle:</span>
                <span className="text-[#181B1F] ml-2">{booking.vehicle_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Customer:</span>
                <span className="text-[#181B1F] ml-2">{booking.customer_info?.full_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Odometer:</span>
                <span className="text-[#181B1F] ml-2">{condition.odometer || 'Not recorded'} km</span>
              </div>
              <div>
                <span className="text-gray-500">Fuel Level:</span>
                <span className="text-[#181B1F] ml-2 capitalize">{condition.fuel_level}</span>
              </div>
              <div>
                <span className="text-gray-500">Damages:</span>
                <span className="text-[#181B1F] ml-2">{condition.damage_marks.length} mark(s)</span>
              </div>
              <div>
                <span className="text-gray-500">Photos:</span>
                <span className="text-[#181B1F] ml-2">{photos.length} photo(s)</span>
              </div>
            </div>
          </div>

          {/* Extra KM Calculation (for return report) */}
          {reportType === 'return' && extraKmData && (
            <div className={`rounded-sm p-4 space-y-2 ${
              extraKmData.extra_km > 0 
                ? 'bg-red-500/10 border border-red-500/30' 
                : 'bg-green-500/10 border border-green-500/30'
            }`}>
              <h4 className={`font-medium ${extraKmData.extra_km > 0 ? 'text-red-400' : 'text-green-400'}`}>
                Kilometer Summary
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Pickup:</span>
                  <span className="text-[#181B1F] ml-2">{extraKmData.pickup_km?.toLocaleString()} km</span>
                </div>
                <div>
                  <span className="text-gray-500">Return:</span>
                  <span className="text-[#181B1F] ml-2">{extraKmData.return_km?.toLocaleString()} km</span>
                </div>
                <div>
                  <span className="text-gray-500">Driven:</span>
                  <span className="text-[#181B1F] ml-2">{extraKmData.km_driven?.toLocaleString()} km</span>
                </div>
                <div>
                  <span className="text-gray-500">Included:</span>
                  <span className="text-[#181B1F] ml-2">{extraKmData.included_km?.toLocaleString()} km</span>
                </div>
              </div>
              {extraKmData.extra_km > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-red-400 font-medium">
                        Extra: {extraKmData.extra_km?.toLocaleString()} km
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        @ CHF {extraKmData.price_per_extra_km}/km
                      </span>
                    </div>
                    <span className="text-red-400 font-bold text-lg">
                      CHF {extraKmData.extra_charge?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">
                    Extra km charges will be processed after report submission
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Return without customer notice */}
          {reportType === 'return' && canSkipCustomerSignature && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <h4 className="text-amber-400 font-medium">Return Without Customer</h4>
                  <p className="text-amber-400/70 text-sm">
                    Customer acknowledged during delivery that return inspection may be done without them.
                    Customer signature is optional.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#181B1F]">
                <Building2 className="w-4 h-4 text-primary" />
                <span className="font-medium">Company Representative</span>
              </div>
              <SignatureCanvas
                label="Admin Signature"
                onSignatureChange={setAdminSignature}
                width={300}
                height={150}
              />
              {adminSignature && (
                <div className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Signed
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#181B1F]">
                <User className="w-4 h-4 text-primary" />
                <span className="font-medium">
                  Customer 
                  {(reportType === 'return' && canSkipCustomerSignature) && (
                    <span className="text-amber-400 text-xs ml-2">(Optional)</span>
                  )}
                </span>
              </div>
              <SignatureCanvas
                label="Customer Signature"
                onSignatureChange={setCustomerSignature}
                width={300}
                height={150}
              />
              {customerSignature && (
                <div className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Signed
                </div>
              )}
              {!customerSignature && reportType === 'return' && canSkipCustomerSignature && (
                <div className="flex items-center gap-1 text-amber-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Will be signed without customer
                </div>
              )}
            </div>
          </div>

          {/* Info Notice */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
            <p className="text-blue-400 text-sm flex items-start gap-2">
              <Send className="w-4 h-4 mt-0.5 shrink-0" />
              Upon submission, the signed report will be automatically emailed to the customer at <strong>{booking.customer_info?.email}</strong>
            </p>
          </div>

          <div className="flex justify-between gap-4 pt-4 border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setStep(1)} 
              className="btn-outline"
            >
              Back to Condition
            </Button>
            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={onClose} className="btn-outline">
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={
                  !adminSignature || 
                  submitting ||
                  (reportType === 'delivery' && !returnAcknowledgment && !customerSignature) ||
                  (reportType === 'return' && !canSkipCustomerSignature && !customerSignature)
                }
                className="btn-primary"
                data-testid="submit-signed-report"
              >
                {submitting ? 'Submitting...' : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Sign & Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveReportGenerator;
